"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface CoverageMapProps {
  /** The city this page is about (centre of the map). */
  city: string;
  county: string;
  /** Nearby cities we also cover, each linking to its own page. */
  cities: { name: string; href: string }[];
  className?: string;
}

const W = 480;
const H = 320;
const CX = 240;
const CY = 160;

/**
 * A bespoke, stylised map: the current city at the centre with the nearby
 * cities we also cover orbiting it (each a link to its own page — useful
 * internal linking), over a faint contour. Lines and nodes draw in when
 * scrolled into view, with a visibility fallback so it is never stuck hidden.
 */
export function CoverageMap({ city, county, cities, className }: CoverageMapProps) {
  const ref = useRef<SVGSVGElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    const fallback = window.setTimeout(() => setShown(true), 1800);
    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
    };
  }, []);

  const places = cities.slice(0, 6);
  const nodes = places.map((c, i) => {
    const angle = ((-90 + i * (360 / Math.max(1, places.length))) * Math.PI) / 180;
    const r = 112 + (i % 2) * 20;
    return {
      ...c,
      x: CX + Math.cos(angle) * r,
      y: CY + Math.sin(angle) * r * 0.7,
      right: Math.cos(angle) >= -0.2,
    };
  });

  const dist = (ax: number, ay: number, bx: number, by: number) =>
    Math.hypot(ax - bx, ay - by);

  const lineStyle = (len: number, i: number): React.CSSProperties => ({
    strokeDasharray: len,
    strokeDashoffset: shown ? 0 : len,
    transition: "stroke-dashoffset 900ms cubic-bezier(0.33,0,0.2,1)",
    transitionDelay: `${120 + i * 90}ms`,
  });

  const nodeStyle = (i: number): React.CSSProperties => ({
    opacity: shown ? 1 : 0,
    transition: "opacity 500ms ease",
    transitionDelay: `${380 + i * 90}ms`,
  });

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${W} ${H}`}
      aria-label={`Map of ${city} and nearby cities we cover across ${county}`}
      className={cn("h-auto w-full", className)}
    >
      {/* Faint contour backdrop */}
      <g className="text-primary" stroke="currentColor" fill="none" opacity={0.12}>
        {Array.from({ length: 5 }).map((_, i) => (
          <ellipse
            key={i}
            cx={CX}
            cy={CY}
            rx={62 + i * 42}
            ry={(62 + i * 42) * 0.7}
            strokeWidth={1}
          />
        ))}
      </g>

      {/* Connecting lines: city → nearby cities */}
      <g className="text-secondary" stroke="currentColor" fill="none" opacity={0.5}>
        {nodes.map((n, i) => (
          <line
            key={n.name}
            x1={CX}
            y1={CY}
            x2={n.x}
            y2={n.y}
            strokeWidth={1}
            style={lineStyle(dist(CX, CY, n.x, n.y), i)}
          />
        ))}
      </g>

      {/* Nearby city nodes (links to their pages) */}
      {nodes.map((n, i) => (
        <a
          key={n.name}
          href={n.href}
          className="group outline-none"
          style={nodeStyle(i)}
        >
          {/* larger transparent hit area */}
          <circle cx={n.x} cy={n.y} r={13} fill="transparent" />
          {/* keyboard focus ring */}
          <circle
            cx={n.x}
            cy={n.y}
            r={11}
            fill="none"
            strokeWidth={2}
            className="stroke-secondary opacity-0 transition-opacity group-focus-visible:opacity-100"
          />
          <circle cx={n.x} cy={n.y} r={8} className="fill-primary" opacity={0.14} />
          <circle
            cx={n.x}
            cy={n.y}
            r={4}
            className="fill-primary transition-colors group-hover:fill-secondary"
          />
          <text
            x={n.right ? n.x + 11 : n.x - 11}
            y={n.y + 3.5}
            textAnchor={n.right ? "start" : "end"}
            className="fill-mid transition-colors group-hover:fill-primary"
            style={{ font: "500 11px var(--font-sans, sans-serif)" }}
          >
            {n.name}
          </text>
        </a>
      ))}

      {/* City marker (centre) */}
      <g
        style={{
          opacity: shown ? 1 : 0,
          transition: "opacity 500ms ease",
          transitionDelay: "200ms",
        }}
      >
        <circle cx={CX} cy={CY} r={20} className="fill-primary" opacity={0.1} />
        <circle cx={CX} cy={CY} r={7} className="fill-secondary" />
        <circle cx={CX} cy={CY} r={3} className="fill-paper" />
        <text
          x={CX}
          y={CY - 28}
          textAnchor="middle"
          className="fill-dark"
          style={{ font: "700 15px var(--font-heading, serif)" }}
        >
          {city}
        </text>
      </g>
    </svg>
  );
}
